import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DemoEnroll, { ENROLL_TEXT } from './DemoEnroll';
import './ParentFeedback.css';

/* =========================================================
   Parent feedback form — opened from the private link in the email / WhatsApp
   (/feedback/:token). No login. English + Arabic.
   ========================================================= */
const API = `${process.env.REACT_APP_API_BASE_URL}/api/feedback`;

const T = {
  en: {
    switch: 'عربي',
    title: (c) => (c ? `How is ${c}'s experience with RoboMind?` : 'How is your experience with RoboMind?'),
    titleDemo: (c) => (c ? `How was ${c}'s free class?` : 'How was the free class?'),
    intro: 'It takes 1 minute. Your honest opinion helps us improve.',
    academy: 'Your overall experience with RoboMind',
    teacher: 'The teacher',
    optional: 'optional',
    recommend: 'How likely are you to recommend RoboMind to a friend?',
    notLikely: 'Not likely',
    veryLikely: 'Very likely',
    intent: 'Would you like to continue with RoboMind?',
    intentYes: "Yes, I'd like to enroll",
    intentMaybe: 'Maybe later',
    intentNo: 'Not for now',
    liked: 'What did you like the most?',
    likedPh: 'The teacher, the projects, the flexible times…',
    improve: 'What can we improve?',
    improvePh: 'Anything we can do better…',
    showAs: 'Your comment may appear on our website. Show it as:',
    needName: 'Please write your name, or choose “A RoboMind parent”.',
    myName: 'My name',
    namePh: 'e.g. Rana K.',
    anonymous: 'A RoboMind parent',
    send: 'Send my feedback',
    sending: 'Sending…',
    needRating: 'Please choose the stars and a score from 0 to 10.',
    error: 'Something went wrong. Please try again.',
    thanks: 'Thank you! 💛',
    ctaText: (c) => `Would you like ${c || 'your child'} to continue? Choose the package, the times and how to pay: 2 minutes.`,
    thanksText: 'Your feedback was sent. It really helps us make RoboMind better for every child.',
    follow: 'Follow us on Instagram',
    done: 'You already answered. Thank you! 💛',
    invalid: 'This link is not valid.',
    invalidText: 'Please check the link in your email, or contact us on WhatsApp.',
    home: 'Go to the website',
    loading: 'Loading…',
    starsLabel: (n) => `${n} out of 5`,
  },
  ar: {
    switch: 'English',
    title: (c) => (c ? `كيف هي تجربة ${c} مع RoboMind؟` : 'كيف هي تجربتكم مع RoboMind؟'),
    titleDemo: (c) => (c ? `كيف كانت الحصة المجانية لـ${c}؟` : 'كيف كانت الحصة المجانية؟'),
    intro: 'تستغرق دقيقة واحدة. رأيكم الصادق يساعدنا على التحسّن.',
    academy: 'تجربتكم العامة مع RoboMind',
    teacher: 'الأستاذ / الأستاذة',
    optional: 'اختياري',
    recommend: 'ما مدى احتمال أن تنصحوا صديقاً بـRoboMind؟',
    notLikely: 'غير محتمل',
    veryLikely: 'محتمل جداً',
    intent: 'هل ترغبون بالاستمرار مع RoboMind؟',
    intentYes: 'نعم، أريد التسجيل',
    intentMaybe: 'ربما لاحقاً',
    intentNo: 'ليس الآن',
    liked: 'ما الذي أعجبكم أكثر؟',
    likedPh: 'الأستاذ، المشاريع، الأوقات المرنة…',
    improve: 'ما الذي يمكننا تحسينه؟',
    improvePh: 'أي شيء يمكننا القيام به بشكل أفضل…',
    showAs: 'قد يظهر تعليقكم على موقعنا. أظهروه باسم:',
    needName: 'الرجاء كتابة اسمكم، أو اختيار «أحد أهالي RoboMind».',
    myName: 'اسمي',
    namePh: 'مثلاً: رنا ك.',
    anonymous: 'أحد أهالي RoboMind',
    send: 'إرسال رأيي',
    sending: 'جارٍ الإرسال…',
    needRating: 'الرجاء اختيار النجوم وعلامة من 0 إلى 10.',
    error: 'حدث خطأ. الرجاء المحاولة مجدداً.',
    thanks: 'شكراً لكم! 💛',
    ctaText: (c) => `هل ترغبون أن يكمل ${c || 'طفلكم'} معنا؟ اختاروا الباقة والأوقات وطريقة الدفع خلال دقيقتين.`,
    thanksText: 'تم إرسال رأيكم. هذا يساعدنا فعلاً على جعل RoboMind أفضل لكل طفل.',
    follow: 'تابعونا على إنستغرام',
    done: 'لقد أجبتم مسبقاً. شكراً لكم! 💛',
    invalid: 'هذا الرابط غير صالح.',
    invalidText: 'الرجاء التحقق من الرابط في البريد الإلكتروني، أو تواصلوا معنا عبر واتساب.',
    home: 'الذهاب إلى الموقع',
    loading: 'جارٍ التحميل…',
    starsLabel: (n) => `${n} من 5`,
  },
};

const startLang = () => {
  try {
    const saved = localStorage.getItem('rm-feedback-lang');
    if (saved === 'ar' || saved === 'en') return saved;
  } catch {
    /* ignore */
  }
  return (navigator.language || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

const Stars = ({ value, onChange, label, t }) => (
  <div className="rmpfb-stars" role="radiogroup" aria-label={label}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        role="radio"
        aria-checked={value === n}
        aria-label={t.starsLabel(n)}
        className={n <= value ? 'is-on' : ''}
        onClick={() => onChange(n)}
      >
        ★
      </button>
    ))}
  </div>
);

const ParentFeedback = () => {
  const { token } = useParams();
  const [lang, setLang] = useState(startLang);
  const [info, setInfo] = useState({ state: 'loading' }); // loading | ready | invalid | done
  const [form, setForm] = useState({
    ratingAcademy: 0,
    ratingTeacher: 0,
    recommend: null,
    enrollIntent: '',
    liked: '',
    improve: '',
    displayMode: 'anonymous', // 'anonymous' = "A RoboMind parent", 'name' = the parent's name
    parentName: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [enroll, setEnroll] = useState(null); // demo families: packages, free times, what they chose
  const [enrollOpen, setEnrollOpen] = useState(false);

  const t = T[lang];
  const rtl = lang === 'ar';

  useEffect(() => {
    try {
      localStorage.setItem('rm-feedback-lang', lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/${encodeURIComponent(token || '')}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success) setInfo({ state: 'invalid' });
        else {
          setInfo({ state: data.submitted ? 'done' : 'ready', kind: data.kind, child: data.child });
          if (data.kind === 'demo') {
            // enroll part (packages, the teacher's free times, what they already chose)
            const er = await fetch(`${API}/${encodeURIComponent(token || '')}/enroll`).catch(() => null);
            const ed = er && er.ok ? await er.json().catch(() => null) : null;
            if (!cancelled && ed?.success) {
              setEnroll(ed);
              if (ed.offer && !ed.state) setEnrollOpen(true); // win-back offer link: straight to the enroll steps
            }
          }
        }
      } catch {
        if (!cancelled) setInfo({ state: 'invalid' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ratingAcademy || form.recommend === null) {
      setError(t.needRating);
      return;
    }
    if (form.displayMode === 'name' && !form.parentName.trim()) {
      setError(t.needName);
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API}/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, shareConsent: true, ratingTeacher: form.ratingTeacher || null, lang }), // the parent chose how to appear; the admin still picks what goes on the homepage
      });
      if (res.status === 409) {
        setInfo((i) => ({ ...i, state: 'done' }));
        return;
      }
      if (!res.ok) throw new Error('failed');
      setSent(true);
      if (form.enrollIntent === 'yes') setEnrollOpen(true); // straight to the 3 enroll steps
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError(t.error);
    } finally {
      setSending(false);
    }
  };

  const demo = info.kind === 'demo';
  const child = info.child;
  const showEnroll = demo && enroll && info.state !== 'invalid' && (enroll.state || enrollOpen);

  return (
    <div className="rmpfb" dir={rtl ? 'rtl' : 'ltr'} lang={lang}>
      <div className="rmpfb-card">
        <div className="rmpfb-top">
          <span className="rmpfb-brand">RoboMind</span>
          <button type="button" className="rmpfb-lang" onClick={() => setLang(rtl ? 'en' : 'ar')}>
            {t.switch}
          </button>
        </div>

        {info.state === 'loading' && <p className="rmpfb-muted">{t.loading}</p>}

        {info.state === 'invalid' && (
          <div className="rmpfb-end">
            <span className="rmpfb-end-icon" aria-hidden="true">🔗</span>
            <h1>{t.invalid}</h1>
            <p>{t.invalidText}</p>
            <Link to="/" className="rmpfb-btn rmpfb-btn--ghost">{t.home}</Link>
          </div>
        )}

        {showEnroll && (
          <DemoEnroll
            token={token}
            data={enroll}
            lang={lang}
            onState={(st) => setEnroll((e) => ({ ...e, state: st }))}
            onClose={enroll.state ? null : () => setEnrollOpen(false)}
          />
        )}

        {!showEnroll && (info.state === 'done' || sent) && (
          <div className="rmpfb-end">
            <span className="rmpfb-end-icon" aria-hidden="true">🎉</span>
            <h1>{sent ? t.thanks : t.done}</h1>
            {sent && <p>{t.thanksText}</p>}
            {demo && enroll && !enroll.state && (
              <div className="rmden-cta">
                <p>{t.ctaText(child)}</p>
                <button type="button" className="rmpfb-btn" onClick={() => setEnrollOpen(true)}>
                  {ENROLL_TEXT[lang].cta(child)}
                </button>
              </div>
            )}
            <a className="rmpfb-btn" href="https://www.instagram.com/robomind.lb" target="_blank" rel="noopener noreferrer">
              {t.follow}
            </a>
          </div>
        )}

        {!showEnroll && info.state === 'ready' && !sent && (
          <form onSubmit={submit} noValidate>
            <h1 className="rmpfb-title">{demo ? t.titleDemo(child) : t.title(child)}</h1>
            <p className="rmpfb-muted">{t.intro}</p>

            <fieldset className="rmpfb-q">
              <legend>{t.academy} <b aria-hidden="true">*</b></legend>
              <Stars value={form.ratingAcademy} onChange={(n) => set({ ratingAcademy: n })} label={t.academy} t={t} />
            </fieldset>

            <fieldset className="rmpfb-q">
              <legend>
                {t.teacher} <small>({t.optional})</small>
              </legend>
              <Stars value={form.ratingTeacher} onChange={(n) => set({ ratingTeacher: n })} label={t.teacher} t={t} />
            </fieldset>

            <fieldset className="rmpfb-q">
              <legend>{t.recommend} <b aria-hidden="true">*</b></legend>
              <div className="rmpfb-scale" role="radiogroup" aria-label={t.recommend}>
                {Array.from({ length: 11 }, (_, n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={form.recommend === n}
                    className={`${form.recommend === n ? 'is-on' : ''} ${n <= 6 ? 'is-low' : n <= 8 ? 'is-mid' : 'is-high'}`}
                    onClick={() => set({ recommend: n })}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="rmpfb-scale-ends">
                <span>{t.notLikely}</span>
                <span>{t.veryLikely}</span>
              </div>
            </fieldset>

            {demo && (
              <fieldset className="rmpfb-q">
                <legend>{t.intent}</legend>
                <div className="rmpfb-chips">
                  {[
                    ['yes', t.intentYes],
                    ['maybe', t.intentMaybe],
                    ['no', t.intentNo],
                  ].map(([key, label]) => (
                    <label key={key} className={`rmpfb-chip ${form.enrollIntent === key ? 'is-on' : ''}`}>
                      <input
                        type="radio"
                        name="intent"
                        checked={form.enrollIntent === key}
                        onChange={() => set({ enrollIntent: key })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <label className="rmpfb-q rmpfb-field">
              <span>{t.liked}</span>
              <textarea
                rows={3}
                maxLength={1000}
                dir="auto"
                placeholder={t.likedPh}
                value={form.liked}
                onChange={(e) => set({ liked: e.target.value })}
              />
            </label>

            <label className="rmpfb-q rmpfb-field">
              <span>{t.improve}</span>
              <textarea
                rows={3}
                maxLength={1000}
                dir="auto"
                placeholder={t.improvePh}
                value={form.improve}
                onChange={(e) => set({ improve: e.target.value })}
              />
            </label>

            {/* how the comment appears on the website: the parent's name, or "A RoboMind parent" */}
            <div className="rmpfb-q rmpfb-share">
              <div className="rmpfb-showas">
                <span>{t.showAs}</span>
                {[
                  ['name', t.myName],
                  ['anonymous', t.anonymous],
                ].map(([key, label]) => (
                  <label key={key} className="rmpfb-radio">
                    <input
                      type="radio"
                      name="displayMode"
                      checked={form.displayMode === key}
                      onChange={() => set({ displayMode: key })}
                    />
                    {label}
                  </label>
                ))}
                {form.displayMode === 'name' && (
                  <input
                    className="rmpfb-input"
                    type="text"
                    maxLength={60}
                    dir="auto"
                    placeholder={t.namePh}
                    value={form.parentName}
                    onChange={(e) => set({ parentName: e.target.value })}
                  />
                )}
              </div>
            </div>

            {error && <p className="rmpfb-error" role="alert">{error}</p>}

            <button type="submit" className="rmpfb-btn rmpfb-submit" disabled={sending}>
              {sending ? t.sending : t.send}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ParentFeedback;
